import React from "react";
import { Button, Space, Tooltip, Typography } from "antd";
import { PlusOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import SectionCard from "../../../components/shared/SectionCard";
import StatusTag from "../../../components/shared/StatusTag";
import InlineLoading from "../../../components/shared/InlineLoading";
import EmptyState from "../../../components/shared/EmptyState";
import { DashboardAppointmentSummary } from "../../../api/healthcare/DashboardApi";
// ⚠️ Direct path, never the src/components/shared barrel — re-exporting through index.ts
// pulls teamApi.ts's import.meta.env read into unrelated test suites (S5 finding).
import PractitionerAvatar, { PractitionerAvatarMember } from "../../../components/shared/PractitionerAvatar";

const { Text } = Typography;

/**
 * The dashboard payload carries a flat practitioner_id/practitioner_name/practitioner_color
 * trio (S7), not a resolved TeamMember. `practitioner_name` is a single "First Last" string
 * (mirroring resolvePatientNames()'s shape) — split it back into the two fields
 * PractitionerAvatar/practitionerInitials() expect. A null practitioner_color on an
 * otherwise-RESOLVED practitioner is passed through as-is: practitionerColour() already
 * falls back to its hashed palette on a null `team_member_info.color`, so no special-casing
 * is needed for that case.
 *
 * `practitioner_id` is local appointment data and always survives (DashboardService.php
 * emits it from the appointment row itself); `practitioner_name`/`practitioner_color` come
 * from auth's bulk resolve call and are null both on a resolve failure AND for any
 * deactivated practitioner (bulkGetPractitioners filters is_active=true). Fix 1
 * (86d40wv5a wave 2, final review): an unresolved practitioner — practitioner_name null —
 * must return null here so PractitionerAvatar renders its neutral grey "?" state, matching
 * the calendar. It must NOT fall through to the hashed fallback palette, which IS a
 * resolved (if collision-prone) identity — see PractitionerAvatar's own docblock.
 */
const toAvatarMember = (appt: DashboardAppointmentSummary): PractitionerAvatarMember | null => {
	if (appt.practitioner_id == null || appt.practitioner_name == null) return null;

	const [firstName = "", ...rest] = appt.practitioner_name.trim().split(/\s+/).filter(Boolean);

	return {
		id: appt.practitioner_id,
		first_name: firstName,
		last_name: rest.join(" "),
		team_member_info: { color: appt.practitioner_color ?? null } as PractitionerAvatarMember["team_member_info"],
	};
};

interface TodaySchedulePanelProps {
	appointments: DashboardAppointmentSummary[];
	isLoading: boolean;
	onOpenAppointment?: (id: number) => void;
}

const formatTime = (iso: string): string =>
	new Date(iso).toLocaleTimeString("en-AU", { hour: "2-digit", minute: "2-digit", hour12: true });

const TodaySchedulePanel: React.FC<TodaySchedulePanelProps> = ({ appointments, isLoading, onOpenAppointment }) => {
	const navigate = useNavigate();

	return (
		<SectionCard
			title="Today's Schedule"
			extra={
				<Button
					size="small"
					icon={<PlusOutlined />}
					onClick={() => navigate("/healthcare/appointments?action=create")}
				>
					Book
				</Button>
			}
			style={{ height: "100%" }}
		>
			{isLoading ? (
				<InlineLoading minHeight={200} message="Loading schedule…" />
			) : appointments.length === 0 ? (
				<EmptyState
					title="No appointments today"
					description="Your schedule is clear for today"
					action={{
						label: "Book Appointment",
						icon: <PlusOutlined />,
						onClick: () => navigate("/healthcare/appointments?action=create"),
					}}
					style={{ padding: "32px 0" }}
				/>
			) : (
				<div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
					{appointments.map((appt, idx) => {
						const typeColor = appt.appointment_type?.color || "var(--mg-primary-bg)";
						const isLast = idx === appointments.length - 1;
						const practitionerMember = toAvatarMember(appt);
						return (
							<div
								key={appt.id}
								data-testid="today-schedule-row"
								onClick={() => onOpenAppointment?.(appt.id)}
								style={{
									display: "flex",
									alignItems: "flex-start",
									gap: 12,
									padding: "8px 0",
									borderBottom: isLast ? "none" : "1px solid var(--mg-border)",
									cursor: "pointer",
								}}
							>
								{/* Time badge — appointment TYPE colour. Distinct axis from the practitioner
								    avatar below; must never be repurposed to carry practitioner_color. */}
								<div
									data-testid="today-schedule-time-badge"
									style={{
										minWidth: 60,
										textAlign: "center",
										padding: "3px 6px",
										borderRadius: 6,
										background: typeColor,
										flexShrink: 0,
									}}
								>
									<Space size={3} align="center">
										<ClockCircleOutlined style={{ fontSize: 10, color: "var(--mg-text-secondary)" }} />
										<Text style={{ fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" }}>
											{formatTime(appt.start_time)}
										</Text>
									</Space>
								</div>

								{/* Practitioner avatar — a SEPARATE colour axis from the time badge above,
								    which keeps rendering appt.appointment_type?.color unchanged. Rendered
								    whenever a practitioner was ASSIGNED to the appointment (practitioner_id
								    present) — never gated on whether that practitioner resolved. An
								    unresolved practitioner (toAvatarMember() returns null — see its
								    docblock) still passes `member={null}` through to PractitionerAvatar,
								    which renders its neutral grey "?" state — the same fallback the
								    calendar (PractitionerCalendar) uses, never the hashed palette. */}
								{appt.practitioner_id != null && (
									<Tooltip title={appt.practitioner_name || undefined}>
										<PractitionerAvatar
											member={practitionerMember}
											size={20}
											style={{ marginTop: 2 }}
											data-testid="today-schedule-practitioner-avatar"
										/>
									</Tooltip>
								)}

								{/* Content */}
								<div style={{ flex: 1, minWidth: 0 }}>
									<Text style={{ fontSize: 12, fontWeight: 600, display: "block" }} ellipsis>
										{appt.patient_name || `Patient #${appt.patient_id}`}
									</Text>
									<Text type="secondary" style={{ fontSize: 12 }} ellipsis>
										{appt.title || appt.appointment_type?.name || appt.category || "Appointment"}
									</Text>
								</div>

								{/* Status */}
								<StatusTag status={appt.status} />
							</div>
						);
					})}
				</div>
			)}
		</SectionCard>
	);
};

export default TodaySchedulePanel;
